import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismTable, PrismToggle } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { gt } from 'ember-truth-helpers';
import MarketTotalRevenueCellsTdChangeWithRelative from 'lumen/components/market/total-revenue/cells/td-change-with-relative';
import type { ReferencePeriod } from 'lumen/controllers/market/total-revenue';
import type { TotalRevenueSegmentationBreakdownItem } from 'lumen/models';
import { PRODUCTION_TYPE_TRANSLATIONS } from 'lumen/utils/constants/total-revenue-constants';
import type Item from 'lumen/utils/datamodels/total-revenue/item';

const COLUMN_PROPERTIES = {
  PRODUCTION_TYPE: 'productionType',
  CLASSIFICATION: 'classification',
  ROOM_NIGHTS: 'roomNights',
  ROOM_NIGHTS_CHANGE: 'roomNightsChange',
  TOTAL_REVENUE_PER_ROOM_NIGHT: 'totalRevenuePerRoomNight',
  TOTAL_REVENUE_PER_ROOM_NIGHT_CHANGE: 'totalRevenuePerRoomNightChange',
  NON_ROOM_REVENUE_PER_ROOM_NIGHT: 'nonRoomRevenuePerRoomNight',
  NON_ROOM_REVENUE_PER_ROOM_NIGHT_CHANGE: 'nonRoomRevenuePerRoomNightChange',
  ROOM_REVENUE_PER_ROOM_NIGHT: 'roomRevenuePerRoomNight',
  ROOM_REVENUE_PER_ROOM_NIGHT_CHANGE: 'roomRevenuePerRoomNightChange',
  TOTAL_REVENUE: 'totalRevenue',
  TOTAL_REVENUE_CHANGE: 'totalRevenueChange',
};

interface MarketTotalRevenueStrategyTableSegmentSignature {
  Args: {
    isLoading: boolean;
    segmentationBreakdownItems: TotalRevenueSegmentationBreakdownItem[];
    selectedReferenceType: ReferencePeriod;
  };
}

export default class MarketTotalRevenueStrategyTableSegment extends Component<MarketTotalRevenueStrategyTableSegmentSignature> {
  @service declare intl: Services['intl'];
  @service('total-revenue/summation')
  declare totalRevenueSummation: Services['total-revenue/summation'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked isCompareEnabled = false;

  initialSorts = [
    {
      valuePath: COLUMN_PROPERTIES.CLASSIFICATION,
      isAscending: false,
    },
  ];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get #matchedTotalRow() {
    const matchedTotalItem =
      this.totalRevenueSummation.getSummatedSegmentationBreakdownItem(
        this.segmentationBreakdownItemsWithoutUnmatched,
      );

    const matchedTotalRow = this.getRowDataForItem(matchedTotalItem);
    matchedTotalRow[COLUMN_PROPERTIES.PRODUCTION_TYPE] = this.intl.t(
      'total_revenue.matched_total',
    );

    return matchedTotalRow;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get #unmatchedTotalRow() {
    const unmatchedItem = this.args.segmentationBreakdownItems.find(
      (item) => item.isUnmatched,
    );
    if (!unmatchedItem) {
      return null;
    }

    const referenceValue = unmatchedItem.referenceValues.find(
      (reference) =>
        reference.referenceType === this.args.selectedReferenceType,
    );

    if (!referenceValue) {
      return null;
    }

    return {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      [COLUMN_PROPERTIES.PRODUCTION_TYPE]: unmatchedItem.formattedName,
      [COLUMN_PROPERTIES.TOTAL_REVENUE]: unmatchedItem.totalRevenue,
      [COLUMN_PROPERTIES.TOTAL_REVENUE_CHANGE]:
        referenceValue.totalRevenueChange,

      displayValue: {
        [COLUMN_PROPERTIES.TOTAL_REVENUE]: unmatchedItem.formattedTotalRevenue,
        [COLUMN_PROPERTIES.TOTAL_REVENUE_CHANGE]: {
          change: referenceValue.formattedTotalRevenueChange,
          relativeChange: referenceValue.formattedTotalRevenueRelativeChange,
        },
      },
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get #valueColumnsWithComparison() {
    return [
      {
        name: this.intl.t('general.room_nights'),
        valuePath: COLUMN_PROPERTIES.ROOM_NIGHTS_CHANGE,
        displayValuePath: `displayValue.${COLUMN_PROPERTIES.ROOM_NIGHTS_CHANGE}`,
        textAlign: 'right' as const,
        tdComponent: MarketTotalRevenueCellsTdChangeWithRelative,
      },
      {
        name: this.intl.t(
          'total_revenue.segment_table.total_revenue_per_room_night',
        ),
        valuePath: COLUMN_PROPERTIES.TOTAL_REVENUE_PER_ROOM_NIGHT_CHANGE,
        displayValuePath: `displayValue.${COLUMN_PROPERTIES.TOTAL_REVENUE_PER_ROOM_NIGHT_CHANGE}`,
        textAlign: 'right' as const,
        tdComponent: MarketTotalRevenueCellsTdChangeWithRelative,
      },
      {
        name: this.intl.t(
          'total_revenue.segment_table.non_room_revenue_per_room_night',
        ),
        valuePath: COLUMN_PROPERTIES.NON_ROOM_REVENUE_PER_ROOM_NIGHT_CHANGE,
        displayValuePath: `displayValue.${COLUMN_PROPERTIES.NON_ROOM_REVENUE_PER_ROOM_NIGHT_CHANGE}`,
        textAlign: 'right' as const,
        tdComponent: MarketTotalRevenueCellsTdChangeWithRelative,
      },
      {
        name: this.intl.t(
          'total_revenue.segment_table.room_revenue_per_room_night',
        ),
        valuePath: COLUMN_PROPERTIES.ROOM_REVENUE_PER_ROOM_NIGHT_CHANGE,
        displayValuePath: `displayValue.${COLUMN_PROPERTIES.ROOM_REVENUE_PER_ROOM_NIGHT_CHANGE}`,
        textAlign: 'right' as const,
        tdComponent: MarketTotalRevenueCellsTdChangeWithRelative,
      },
      {
        name: this.intl.t('total_revenue.total_revenue'),
        valuePath: COLUMN_PROPERTIES.TOTAL_REVENUE_CHANGE,
        displayValuePath: `displayValue.${COLUMN_PROPERTIES.TOTAL_REVENUE_CHANGE}`,
        textAlign: 'right' as const,
        tdComponent: MarketTotalRevenueCellsTdChangeWithRelative,
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get #valueColumnsWithoutComparison() {
    return [
      {
        name: this.intl.t('general.room_nights'),
        valuePath: COLUMN_PROPERTIES.ROOM_NIGHTS,
        textAlign: 'right' as const,
      },
      {
        name: this.intl.t(
          'total_revenue.segment_table.total_revenue_per_room_night',
        ),
        valuePath: COLUMN_PROPERTIES.TOTAL_REVENUE_PER_ROOM_NIGHT,
        displayValuePath: `displayValue.${COLUMN_PROPERTIES.TOTAL_REVENUE_PER_ROOM_NIGHT}`,
        textAlign: 'right' as const,
      },
      {
        name: this.intl.t(
          'total_revenue.segment_table.non_room_revenue_per_room_night',
        ),
        valuePath: COLUMN_PROPERTIES.NON_ROOM_REVENUE_PER_ROOM_NIGHT,
        displayValuePath: `displayValue.${COLUMN_PROPERTIES.NON_ROOM_REVENUE_PER_ROOM_NIGHT}`,
        textAlign: 'right' as const,
      },
      {
        name: this.intl.t(
          'total_revenue.segment_table.room_revenue_per_room_night',
        ),
        valuePath: COLUMN_PROPERTIES.ROOM_REVENUE_PER_ROOM_NIGHT,
        displayValuePath: `displayValue.${COLUMN_PROPERTIES.ROOM_REVENUE_PER_ROOM_NIGHT}`,
        textAlign: 'right' as const,
      },
      {
        name: this.intl.t('total_revenue.total_revenue'),
        valuePath: COLUMN_PROPERTIES.TOTAL_REVENUE,
        displayValuePath: `displayValue.${COLUMN_PROPERTIES.TOTAL_REVENUE}`,
        textAlign: 'right' as const,
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get columns() {
    const valueColumns = this.isCompareEnabled
      ? this.#valueColumnsWithComparison
      : this.#valueColumnsWithoutComparison;

    return [
      {
        name: this.intl.t(PRODUCTION_TYPE_TRANSLATIONS.segment),
        valuePath: COLUMN_PROPERTIES.PRODUCTION_TYPE,
        textAlign: 'left' as const,
      },
      {
        name: this.intl.t('total_revenue.segment_table.classification'),
        valuePath: COLUMN_PROPERTIES.CLASSIFICATION,
        textAlign: 'left' as const,
      },
      ...valueColumns,
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get footerRows() {
    const rows = [this.#matchedTotalRow];

    if (this.#unmatchedTotalRow) {
      rows.push(this.#unmatchedTotalRow);
    }

    return rows.map((row) => {
      row['isFooterRow'] = true;
      row['className'] = 'table-row--footer';
      return row;
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rows() {
    return this.segmentationBreakdownItemsWithoutUnmatched.map(
      (segmentationBreakdownItem) =>
        this.getRowDataForItem(segmentationBreakdownItem),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get segmentationBreakdownItemsWithoutUnmatched() {
    return this.args.segmentationBreakdownItems.filter(
      (item) => !item.isUnmatched,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getRowDataForItem(
    segmentationBreakdownItem: Item | TotalRevenueSegmentationBreakdownItem,
  ) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const formattedName =
      'formattedName' in segmentationBreakdownItem
        ? segmentationBreakdownItem.formattedName
        : '';

    const translatedClassification =
      'translatedClassification' in segmentationBreakdownItem
        ? segmentationBreakdownItem.translatedClassification
        : '';

    const row = {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      [COLUMN_PROPERTIES.PRODUCTION_TYPE]: formattedName,
      [COLUMN_PROPERTIES.CLASSIFICATION]: translatedClassification,
      [COLUMN_PROPERTIES.ROOM_NIGHTS]: segmentationBreakdownItem.nrOfRooms,
      [COLUMN_PROPERTIES.TOTAL_REVENUE_PER_ROOM_NIGHT]:
        segmentationBreakdownItem.totalRevenuePerRoomNight,
      [COLUMN_PROPERTIES.NON_ROOM_REVENUE_PER_ROOM_NIGHT]:
        segmentationBreakdownItem.totalNonRoomRevenuePerRoomNight,
      [COLUMN_PROPERTIES.ROOM_REVENUE_PER_ROOM_NIGHT]:
        segmentationBreakdownItem.totalRoomRevenuePerRoomNight,
      [COLUMN_PROPERTIES.TOTAL_REVENUE]: segmentationBreakdownItem.totalRevenue,

      displayValue: {
        [COLUMN_PROPERTIES.TOTAL_REVENUE_PER_ROOM_NIGHT]:
          segmentationBreakdownItem.formattedTotalRevenuePerRoomNight,
        [COLUMN_PROPERTIES.NON_ROOM_REVENUE_PER_ROOM_NIGHT]:
          segmentationBreakdownItem.formattedTotalNonRoomRevenuePerRoomNight,
        [COLUMN_PROPERTIES.ROOM_REVENUE_PER_ROOM_NIGHT]:
          segmentationBreakdownItem.formattedTotalRoomRevenuePerRoomNight,
        [COLUMN_PROPERTIES.TOTAL_REVENUE]:
          segmentationBreakdownItem.formattedTotalRevenue,
      },
    };

    const selectedReferenceItem =
      segmentationBreakdownItem.referenceValues.find(
        (reference) =>
          reference.referenceType === this.args.selectedReferenceType,
      );

    if (!selectedReferenceItem) {
      return row;
    }

    return {
      ...row,
      [COLUMN_PROPERTIES.ROOM_NIGHTS_CHANGE]:
        selectedReferenceItem.nrOfRoomsChange,
      [COLUMN_PROPERTIES.TOTAL_REVENUE_PER_ROOM_NIGHT_CHANGE]:
        selectedReferenceItem.totalRevenuePerRoomNightChange,
      [COLUMN_PROPERTIES.NON_ROOM_REVENUE_PER_ROOM_NIGHT_CHANGE]:
        selectedReferenceItem.totalNonRoomRevenuePerRoomNightChange,
      [COLUMN_PROPERTIES.ROOM_REVENUE_PER_ROOM_NIGHT_CHANGE]:
        selectedReferenceItem.totalRoomRevenuePerRoomNightChange,
      [COLUMN_PROPERTIES.TOTAL_REVENUE_CHANGE]:
        selectedReferenceItem.totalRevenueChange,

      displayValue: {
        ...row.displayValue,
        [COLUMN_PROPERTIES.ROOM_NIGHTS_CHANGE]: {
          change: selectedReferenceItem.formattedNrOfRoomsChange,
          relativeChange:
            selectedReferenceItem.formattedNrOfRoomsRelativeChange,
        },
        [COLUMN_PROPERTIES.TOTAL_REVENUE_PER_ROOM_NIGHT_CHANGE]: {
          change: selectedReferenceItem.formattedTotalRevenuePerRoomNightChange,
          relativeChange:
            selectedReferenceItem.formattedTotalRevenuePerRoomNightRelativeChange,
        },
        [COLUMN_PROPERTIES.NON_ROOM_REVENUE_PER_ROOM_NIGHT_CHANGE]: {
          change:
            selectedReferenceItem.formattedTotalNonRoomRevenuePerRoomNightChange,
          relativeChange:
            selectedReferenceItem.formattedTotalNonRoomRevenuePerRoomNightRelativeChange,
        },
        [COLUMN_PROPERTIES.ROOM_REVENUE_PER_ROOM_NIGHT_CHANGE]: {
          change:
            selectedReferenceItem.formattedTotalRoomRevenuePerRoomNightChange,
          relativeChange:
            selectedReferenceItem.formattedTotalRoomRevenuePerRoomNightRelativeChange,
        },
        [COLUMN_PROPERTIES.TOTAL_REVENUE_CHANGE]: {
          change: selectedReferenceItem.formattedTotalRevenueChange,
          relativeChange:
            selectedReferenceItem.formattedTotalRevenueRelativeChange,
        },
      },
    };
  }

  // eslint-disable-next-line max-params -- signature dictated by ember-table's @sortFunction
  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  sortFunction(
    rowA: Record<string, unknown>,
    rowB: Record<string, unknown>,
    sorts: { isAscending: boolean; valuePath: string }[],
    compare: (
      valueA: unknown,
      valueB: unknown,
      sortEmptyLast: boolean,
    ) => number,
    sortEmptyLast: boolean,
  ) {
    if (rowA['isFooterRow'] || rowB['isFooterRow']) {
      return 0;
    }

    let compareValue = 0;

    sorts.some(({ valuePath, isAscending }) => {
      const valueA = rowA[valuePath];
      const valueB = rowB[valuePath];

      const ascendingSign = isAscending ? 1 : -1;

      compareValue = compare(valueA, valueB, sortEmptyLast) * ascendingSign;

      return compareValue !== 0;
    });

    return compareValue;
  }

  @action toggleCompare(isEnabled: boolean): void {
    this.isCompareEnabled = isEnabled;

    this.usageAnalytics.track('Drilldown Compare Changed', {
      eventTrigger: 'drilldown-compare-changed',
      isEnabled,
    });
  }

  <template>
    <div class="mb-400 flex justify-between">
      <h3>{{t "general.drilldown"}}</h3>
      <PrismToggle
        @isChecked={{this.isCompareEnabled}}
        @label={{t "general.compare"}}
        @onChange={{this.toggleCompare}}
      />
    </div>

    <PrismTable
      @columns={{this.columns}}
      @enableAutoSortsUpdate={{true}}
      @footerRows={{this.footerRows}}
      @initialSorts={{this.initialSorts}}
      @isLoading={{@isLoading}}
      @nrOfEmptyRows={{5}}
      @nrOfLoadingRows={{10}}
      @rows={{this.rows}}
      {{! @glint-expect-error: sortFunction types are too strict }}
      @sortFunction={{this.sortFunction}}
      class="table-segment
        {{if (gt this.rows.length 15) 'table-segment--scrollable'}}"
    />
  </template>
}
